import { ChangeEvent } from 'react';
import { Control, FieldValues, Path, useController } from 'react-hook-form';
import styled from 'styled-components';

import { Input } from '@/components/Forms';
import { ensureStringContainsPositiveDigitsOnly } from '@/utils/helpers';

const InputGroup = styled.div`
  position: relative;
`;

const Buttons = styled.div`
  position: absolute;
  width: 46px;
  right: 0;
  top: 0;
  bottom: 0;
  height: 100%;
  border-left: 1px solid ${({ theme }) => theme.colors.grayscale};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  transition: 0.2s ease-out border-color;
`;

const ArrowButton = styled.button`
  width: 8px;
  height: 5px;
  padding: 3px;
  box-sizing: content-box;
  display: flex;
`;

const StyledInput = styled(Input)`
  &:focus,
  &:hover {
    + ${Buttons} {
      border-color: ${({ theme }) => theme.colors.gray};
    }
  }
`;

interface Props<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  disabled?: boolean;
}

const IntegerField = <T extends FieldValues>({
  control,
  name,
  disabled = false,
}: Props<T>) => {
  const {
    field: { onChange, value },
  } = useController({
    name,
    control,
  });

  const increment = () => {
    const newValue = parseInt(value ?? '0') + 1;

    onChange(newValue.toString());
  };

  const decrement = () => {
    const newValue = parseInt(value ?? '0') - 1;

    onChange(newValue.toString());
  };

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!ensureStringContainsPositiveDigitsOnly(e.target.value)) return;

    onChange(e.target.value);
  };

  return (
    <InputGroup>
      <StyledInput
        onChange={onInputChange}
        name="min"
        value={value}
        disabled={disabled}
      />
      <Buttons>
        <ArrowButton type="button" onClick={increment} disabled={disabled}>
          <svg
            width="8"
            height="5"
            viewBox="0 0 8 5"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M4 0L0 5H8L4 0Z" fill="currentColor" />
          </svg>
        </ArrowButton>
        <ArrowButton type="button" onClick={decrement} disabled={disabled}>
          <svg
            width="8"
            height="5"
            viewBox="0 0 8 5"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M4 5L8 0H0L4 5Z" fill="currentColor" />
          </svg>
        </ArrowButton>
      </Buttons>
    </InputGroup>
  );
};

export default IntegerField;
